'use client';

import { Button } from '@shared/ui/src/components/buttons/Button';
import { X, Shield, Lock, Database, Eye } from 'lucide-react';
import type { RoleResourcePermission } from '@shared/db/src/domains/user';

// Custom permission badge component
export const PermissionBadge = ({ 
  item,
  onRemove,
  isRemoving = false
}: { 
  item: any; // Permission data
  onRemove?: () => void;
  isRemoving?: boolean;
}) => {
  const permission = item as RoleResourcePermission;
  const getVerbIcon = (verb: string) => {
    switch (verb.toLowerCase()) {
      case 'read': return <Eye className="w-3 h-3" />;
      case 'create': case 'update': case 'delete': return <Database className="w-3 h-3" />;
      case 'manage': return <Shield className="w-3 h-3" />;
      default: return <Lock className="w-3 h-3" />;
    }
  };

  const getVerbColor = (verb: string) => {
    switch (verb.toLowerCase()) {
      case 'read': return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
      case 'create': return 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100';
      case 'update': return 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100';
      case 'delete': return 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100';
      case 'manage': return 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100';
      default: return 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100';
    }
  };

  const formatResourceName = (resource: string) => {
    return resource.replace(/^db_/, '').replace(/_/g, ' ').replace(/\./g, ' › ');
  };

  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${getVerbColor(permission.verb)}`}>
      {getVerbIcon(permission.verb)}
      <span className="font-medium">{permission.verb}</span>
      <span className="text-gray-500">on</span>
      <span>{formatResourceName(permission.resource)}</span>
      <span className="text-xs opacity-75">({permission.data_access})</span>
      {onRemove && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={isRemoving}
          className="p-0 h-4 w-4 ml-1 rounded-full hover:bg-red-500 hover:text-white transition-colors duration-200 disabled:opacity-50"
        >
          {isRemoving ? (
            <div className="w-2 h-2 border border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <X className="h-3 w-3" />
          )}
        </Button>
      )}
    </div>
  );
}; 